import { Typography } from '@design-system/components/typography/typography';
import { IconButton } from '@design-system/components/button/iconButton';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import { CommentsViewMode } from '../model/comments.viewModel';
import { VisibilityChip } from './VisibilityChip';
import * as css from './NavigationHeader.css';

type NavigationHeaderProps = {
  viewMode: CommentsViewMode;
  canGoBack: boolean;
  onBack: () => void;
  hiddenCount?: number;
  visibleCount?: number;
  threadHiddenCount?: number;
  threadVisibleCount?: number;
  shouldShowHiddenChip?: boolean;
  shouldShowThreadHiddenChip?: boolean;
  onNavigateToHidden?: () => void;
  onNavigateToThreadHidden?: () => void;
};

const getHeaderInfo = (viewMode: CommentsViewMode) => {
  switch (viewMode) {
    case 'hidden':
      return {
        title: 'Hidden Comments',
        subtitle: 'These comments are not visible for your audience',
      };
    case 'thread':
      return {
        title: 'Thread',
        subtitle: undefined,
      };
    case 'thread-hidden':
      return {
        title: 'Hidden Thread Replies',
        subtitle: 'These replies are not visible for your audience',
      };
    case 'comments':
    default:
      return {
        title: 'Comments',
        subtitle: undefined,
      };
  }
};

export const NavigationHeader = ({
  viewMode,
  canGoBack,
  onBack,
  hiddenCount = 0,
  visibleCount = 0,
  threadHiddenCount = 0,
  threadVisibleCount = 0,
  shouldShowHiddenChip = false,
  shouldShowThreadHiddenChip = false,
  onNavigateToHidden,
  onNavigateToThreadHidden,
}: NavigationHeaderProps) => {
  const { title, subtitle } = getHeaderInfo(viewMode);

  return (
    <div className={css.navigationHeader}>
      <div className={css.headerContent}>
        <div className={css.titleContainer}>
          {canGoBack && (
            <IconButton
              icon={faArrowLeft}
              onClick={onBack}
              variant="text"
              color="neutral"
              className={css.backButton}
            />
          )}
          <Typography variant="title" fontWeight="medium">
            {title}
          </Typography>
        </div>

        {shouldShowHiddenChip && onNavigateToHidden && (
          <VisibilityChip
            showHidden={false}
            visibleCount={visibleCount}
            hiddenCount={hiddenCount}
            onClick={onNavigateToHidden}
          />
        )}

        {shouldShowThreadHiddenChip && onNavigateToThreadHidden && (
          <VisibilityChip
            showHidden={false}
            visibleCount={threadVisibleCount}
            hiddenCount={threadHiddenCount}
            onClick={onNavigateToThreadHidden}
          />
        )}
      </div>
      {subtitle && (
        <Typography
          variant="body"
          size="medium"
          color="N-100"
          fontWeight="normal"
          className={css.subtitle}
        >
          {subtitle}
        </Typography>
      )}
    </div>
  );
};
